// QEW Field Access rules. Words: STEP3_SIGNIN_APPROVED.md (approved 2026-09-29).
// Dashboard top block: QEW_DASHBOARD_WORDS.md (approved 2026-10-06).
// Shared by the Field Access screens and the qewSignIn server step, and unit tested.
// Nothing here reads or writes the database.
//
// No PIN. Bag tag scan or typed QEW ID, then "Is this you?" once per visit.
// A visit ends on Sign Out or after 1 hour with no activity (access.ts).

import { AUTHORITIES, type Authority, type SignInClaims } from "./badge";
import type { TagView } from "./claim";

/**
 * One phone, one sign-in, shared by every tab. A page for one QEW refuses to save
 * once another QEW signs in on the phone (Robert, 2026-10-06).
 */
export const OTHER_SIGN_IN = "Someone else signed in on this phone. Scan your bag tag again.";

/** The server's check on every Dashboard save. Null when the save is for the signed-in QEW. */
export function sameQewProblem(signedInBadgeId: string, forBadgeId: unknown): string | null {
  return forBadgeId !== undefined && forBadgeId !== signedInBadgeId ? OTHER_SIGN_IN : null;
}

/** Authorities a QEW sign-in never carries, whatever the badge says. */
const ADMIN_ONLY: readonly string[] = ["admin", "owner", "ieaWalk"];

/** Four number-pad digits into a QEW ID: 0017 -> 00-QEW17. Null until all four are in. */
export function typedQewId(digits: string): string | null {
  return /^\d{4}$/.test(digits) ? `${digits.slice(0, 2)}-QEW${digits.slice(2)}` : null;
}

/** What a tag check finds. "ask" signs in at once ("Is this you?" removed 2026-10-09); the others stop. */
export type NameCheck =
  | { kind: "ask"; tagId: string; holderName: string; siteName: string }
  | { kind: "unclaimed"; tagId: string; message: string }
  | { kind: "stop"; tagId: string; message: string };

export function nameCheckOf(view: TagView): NameCheck {
  switch (view.kind) {
    case "claimed":
      return { kind: "ask", tagId: view.tagId, holderName: view.holderName, siteName: view.siteName };
    case "unclaimed":
      return { kind: "unclaimed", tagId: view.tagId, message: `${view.tagId} is not claimed yet. Claim it at a training class first.` };
    case "disabled":
      return { kind: "stop", tagId: view.tagId, message: `${view.tagId} is out of service. See your ESP Manager.` };
    default:
      return { kind: "stop", tagId: view.tagId, message: `${view.tagId} is not in the system. Check the number on your tag.` };
  }
}

/**
 * The sign-in copy of a QEW badge. Null unless the badge is Active.
 * Site comes from the tag ID. Admin-only authorities are dropped.
 */
export function qewClaims(company: string, tagId: string, badge: Record<string, unknown> | null): SignInClaims | null {
  if (!badge || badge.Status !== "Active" || !/^\d{2}-QEW\d{2}$/.test(tagId)) return null;
  const raw = Array.isArray(badge.Authorities) ? (badge.Authorities as unknown[]) : [];
  const authorities = raw.filter(
    (a): a is Authority => typeof a === "string" && (AUTHORITIES as readonly string[]).includes(a) && !ADMIN_ONLY.includes(a),
  );
  return {
    company,
    badgeId: tagId,
    holderName: String(badge["Holder Name"] ?? "").trim() || tagId,
    sites: [tagId.slice(0, 2)],
    authorities,
  };
}

/** True for a QEW bag tag sign-in, as opposed to an admin badge. */
export function isQewSignIn(claims: SignInClaims | null): boolean {
  return Boolean(claims && /^\d{2}-QEW\d{2}$/.test(claims.badgeId) && !claims.authorities.includes("admin"));
}

/** The person fields the top block reads. */
export interface PersonStatus {
  isActive: boolean;
  lotoTrainedOn: string | null;
  lotoVerifiedOn: string | null;
  /** A verified date kept when the QEW reports a new one. Still counts toward Active. */
  lotoLastVerifiedOn?: string | null;
}

/**
 * The Dashboard top block. Active = Nodes 1 to 4 done once plus LOTO verified once,
 * or carried Active from the Sheet. A later expiry never takes it away.
 * Node 4 joined the rule when it began closing itself on the demo JSPs (JSP_WRITES_WORDS.md call 4, 2026-10-09; two JSPs since 2026-10-12).
 */
export function dashboardStatus(trainingComplete: boolean, p: PersonStatus): { tone: "green" | "yellow"; text: string } {
  const active = p.isActive || (trainingComplete && Boolean(p.lotoVerifiedOn || p.lotoLastVerifiedOn));
  const loto = p.lotoVerifiedOn ? "LOTO verified" : p.lotoTrainedOn ? "LOTO pending" : "Waiting on LOTO";
  return active ? { tone: "green", text: `✓ Qualified, Active · ${loto}` } : { tone: "yellow", text: `● In training · ${loto}` };
}

// ---- The asset gate (Stage 4b, SIGNIN_PASS_WORDS.md, approved by Robert 2026-10-10) ----
// The approved ladder: demo assets open after Node 3; every real asset needs Active
// (Nodes 1 to 4 and a verified LOTO date, or carried Active from the Sheet).
// Expired training on an Active QEW warns and never blocks. The server checks this; the page only explains it.

export interface GateNeed {
  title: string;
  line: string;
}

export interface AssetGate {
  /** "Opens when you are Active" or "Demo assets open after Node 3". */
  sub: string;
  /** Only what is missing, with who acts on it. */
  needs: GateNeed[];
  /** One grey line at the bottom, e.g. "Done: Nodes 1 to 4". Blank when nothing is done. */
  done: string;
}

export interface GateInput {
  isDemo: boolean;
  /** Node IDs done at some point, current or not (done once). */
  nodesDone: readonly string[];
  isActive: boolean;
  lotoTrainedOn: string | null;
  lotoVerifiedOn: string | null;
  lotoLastVerifiedOn?: string | null;
  lotoNoneReported?: boolean;
}

const NODE_NAMES: Record<string, string> = {
  node1: "Hazards and ESP",
  node2: "Shock, Arc Flash and PPE",
  node3: "Meters and LOTO",
  node4: "Field demonstration",
};

function usDay(iso: string): string {
  const [y, m, d] = iso.split("-");
  return y && m && d ? `${m}/${d}/${y}` : iso;
}

function doneNodes(done: readonly string[], upTo: number): string {
  const n = ["node1", "node2", "node3", "node4"].slice(0, upTo).filter((id) => done.includes(id)).length;
  if (n === 0) return "";
  return n === 1 ? "Done: Node 1" : n === 2 ? "Done: Nodes 1 and 2" : `Done: Nodes 1 to ${n}`;
}

/** Null when he may work here. Otherwise the Not Yet Qualified screen. */
export function assetGateOf(g: GateInput): AssetGate | null {
  const ids = ["node1", "node2", "node3", "node4"];
  const next = (upTo: number) => ids.slice(0, upTo).find((id) => !g.nodesDone.includes(id));
  const nodeNeed = (id: string): GateNeed => ({ title: `Node ${id.slice(4)} · ${NODE_NAMES[id]}`, line: id === "node4" ? "Next up: the two demo JSPs." : "Next up, in class with your instructor." });

  if (g.isDemo) {
    const missing = next(3);
    if (!missing) return null;
    return { sub: "Demo assets open after Node 3", needs: [nodeNeed(missing)], done: doneNodes(g.nodesDone, 3) };
  }
  const verified = Boolean(g.lotoVerifiedOn || g.lotoLastVerifiedOn);
  const missing = next(4);
  if (g.isActive || (!missing && verified)) return null;
  const needs: GateNeed[] = [];
  if (missing) needs.push(nodeNeed(missing));
  if (!verified) {
    needs.push(
      g.lotoTrainedOn && !g.lotoNoneReported
        ? { title: "LOTO date verified", line: `You reported ${usDay(g.lotoTrainedOn)}. Your manager, site manager or Safety confirms it.` }
        : { title: "LOTO date", line: "Not on file. Report it on your LOTO card." },
    );
  }
  return { sub: "Opens when you are Active", needs, done: doneNodes(g.nodesDone, 4) };
}

/** The server's refusal when the gate is shut. */
export function assetGateRefusal(gate: AssetGate): string {
  return `Not yet qualified for this equipment. ${gate.sub}.`;
}
